import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Flag, RotateCw, Video, VideoOff, Volume2, VolumeX } from 'lucide-react';
import { FilterType, VideoFilters } from '../VideoFilters';

interface VideoControlButtonsProps {
  // Peer controls
  isPeerAudioMuted: boolean;
  isPeerVideoHidden: boolean;
  togglePeerAudioMute: () => void;
  togglePeerVideoHide: () => void;

  // Filter
  onFilterChange: (filter: FilterType, intensity: number, filterStyle: string) => void;

  // Optional controls
  onReport?: () => void;
  onSwapPositions?: () => void;

  // Visibility flags
  showSwapButton?: boolean;
  showReportButton?: boolean;
}

/**
 * Declarative overlay control buttons for video area
 * Renders filters, peer audio/video controls, report, and swap buttons
 */
export const VideoControlButtons: React.FC<VideoControlButtonsProps> = ({
  isPeerAudioMuted,
  isPeerVideoHidden,
  togglePeerAudioMute,
  togglePeerVideoHide,
  onFilterChange,
  onReport,
  onSwapPositions,
  showSwapButton = false,
  showReportButton = false
}) => {
  return (
    <div className="absolute top-4 right-4 flex flex-col space-y-2 z-20">
      {/* Swap Positions Button */}
      {showSwapButton && onSwapPositions && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="bg-[#1E293B] hover:bg-[#273449] text-white border border-slate-700 p-2"
                onClick={onSwapPositions}
              >
                <RotateCw className="w-4 h-4"/>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              Swap video positions
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Video Filters */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <VideoFilters
                videoRef={useRef<HTMLVideoElement>(null)}
                canvasRef={useRef<HTMLCanvasElement>(null)}
                isLocalVideo={true}
                onFilterChange={onFilterChange}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="left">
            Video filters
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Peer Audio Control */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`${isPeerAudioMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-[#1E293B] hover:bg-[#273449]'} text-white border border-slate-700 p-2`}
              onClick={togglePeerAudioMute}
            >
              {isPeerAudioMuted ? <VolumeX className="w-4 h-4"/> : <Volume2 className="w-4 h-4"/>}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            {isPeerAudioMuted ? 'Unmute peer' : 'Mute peer'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Peer Video Control */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`${isPeerVideoHidden ? 'bg-red-600 hover:bg-red-700' : 'bg-[#1E293B] hover:bg-[#273449]'} text-white border border-slate-700 p-2`}
              onClick={togglePeerVideoHide}
            >
              {isPeerVideoHidden ? <VideoOff className="w-4 h-4"/> : <Video className="w-4 h-4"/>}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            {isPeerVideoHidden ? 'Show peer video' : 'Hide peer video'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Report Button */}
      {showReportButton && onReport && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="bg-[#1E293B] hover:bg-[#273449] text-white border border-slate-700 p-2"
                onClick={onReport}
              >
                <Flag className="w-4 h-4"/>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              Report inappropriate behavior
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
